import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Link, Navigate, useNavigate } from 'react-router-dom'; // Keep Link and Navigate for now, though Link is only used for external-like links
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Paper,
  CircularProgress // Import CircularProgress if used for loading state on button
} from '@mui/material';
import { styled } from '@mui/system';


// Styled Paper component for consistent styling
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
  maxWidth: '400px',
  margin: 'auto',
  marginTop: theme.spacing(8),
}));

// Login component receiving necessary props
// Note: login, sendOtp, and verifyOtp props are defined but not used in the current code logic.
const Login = ({ login, isAuthenticated, sendOtp, verifyOtp }) => {
  const [loginFormData, setLoginFormData] = useState({
    email: "",
    password: "",
  });

  // Combined error state
  const [error, setError] = useState(null); // Initialize error as null
  // Added loading state for the login button
  const [loading, setLoading] = useState(false);

  const emailInputRef = useRef(null); // Ref for email input
  const navigate = useNavigate(); // Hook for navigation

  // Focus email input on component mount
  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
    // Empty dependency array ensures this runs only once on mount
  }, []);

  // Destructure form data for easier access
  const { email, password } = loginFormData;

  // Handle input changes
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing again
    setError(null);
  };

  // Handle form submission
  const onSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission
    setError(null); // Clear previous errors
    setLoading(true); // Set loading state

    // Basic validation
    if (!email || !password) {
      setError('Please fill in all fields');
      setLoading(false); // Stop loading
      return;
    }

    try {
      // Call the login function passed as a prop from App.js
      const result = await login(email, password); 

      if (result && result.success) {
        // App.js's login function handles setting token and user state.
        console.log('Login successful via App.js. Navigating to dashboard.');
        navigate('/dashboard'); 
      } else {
        // Handle login failure based on the message from App.js's login function
        setError(result.message || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      // This catch block handles errors from calling the `login` prop or other unexpected issues.
      console.error("Error during login process in Login.js:", error);
      const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      // Ensure loading state is always turned off after the request
      setLoading(false);
    }
  };

  // Handler for navigating to OTP login page
  const handleOtpLoginClick = () => {
    navigate('/login-otp');
  };

  // Redirect to dashboard if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }

  // Render the login form
  return (
    <Container component="main" maxWidth="sm">
      <StyledPaper elevation={3}>
        <Typography component="h1" variant="h5" align="center" color="primary" gutterBottom>
          Sign In
        </Typography>
        <Typography variant="body1" align="center" color="textSecondary" gutterBottom>
          Log in to Your Account
        </Typography>

        {/* Display error message if error state is not null */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={onSubmit} noValidate>
          <TextField
            inputRef={emailInputRef} // Attach ref to email input
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            value={email}
            onChange={handleLoginChange}
            // Add error prop for visual feedback if needed based on validation
            // error={!!(error && !email)} // Example: show error if email is empty and form was submitted
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={handleLoginChange}
            // Add error prop for visual feedback if needed based on validation
            // error={!!(error && !password)} // Example: show error if password is empty and form was submitted
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            disabled={loading} // Disable button while loading
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null} // Show spinner when loading
          >
            {loading ? 'Logging In...' : 'Login'} {/* Change button text while loading */}
          </Button>

           <Button
            fullWidth
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleOtpLoginClick}
            disabled={loading} // Disable OTP button while main login is loading
          >
             Login via OTP
          </Button>
        </Box>
        <Box mt={2} textAlign="center">
          {/* Using Link from react-router-dom for navigation */}
          <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
            <Typography color="primary" variant="body2">Forgot Password?</Typography>
          </Link>
        </Box>
        <Box mt={1} textAlign="center">
          <Typography variant="body2">
            Don't have an account?{' '}
            {/* Using Link from react-router-dom for navigation */}
            <Link to="/register" style={{ textDecoration: 'none' }}>
              <Typography component="span" color="primary" variant="body2">Sign Up</Typography>
            </Link>
          </Typography>
        </Box>
      </StyledPaper>
    </Container>
  );
};

export default Login;
